// Global imports
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react'
import { useFetcher, useSearchParams } from '@remix-run/react'
import { useBrendaCharges, useDebugLogger, useRootEnvBoolean } from '~/hooks'
import {
  buildCartLines,
  groupCartLines,
  getCartItemsCount,
  getCartHasFreeDelivery,
  getCartHasIssues,
  getCartHasChanges,
  buildTitleByLineId,
} from '~/utils/basket'

// Types
import type { ReactNode } from 'react'
import type { HssBankHolidayItem, UserAddressProps } from '~/types'
import type {
  BrendaChargesErrorState,
  BrendaChargesResult,
  BrendaChargesSubmit,
} from '~/types/brendaCharges'
import type {
  LunarBasket,
  BasketLineData,
  BasketLineGrouped,
  BasketLineItemProps,
  BasketRevalidation,
  BasketRevalidationIssue,
  BasketRevalidationChange,
  PickupSearchCache,
} from '~/types/basket'

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

export interface BasketContextValue {
  // Raw cart data from loader
  cart: LunarBasket | null

  // Derived / processed data
  allLines: BasketLineData[]
  lineItems: BasketLineGrouped[]
  itemsCount: number
  hasFreeDelivery: boolean
  hasSameDayDelivery: boolean

  // Revalidation state
  revalidation: BasketRevalidation | null
  hasIssues: boolean
  hasChanges: boolean
  titleByLineId: Map<string, string>

  // Client-side issues raised by basket flows (fulfilment switch,
  // qty change) when the target state would leave an optional add-on
  // unfulfillable. Merged into `hasIssues` and rendered by
  // `BasketNotices` alongside loader-driven revalidation issues.
  clientIssues: BasketRevalidationIssue[]
  setClientIssues: (issues: BasketRevalidationIssue[]) => void
  clearClientIssues: () => void

  // Client-side auto-correction notices raised by basket flows when
  // an action they took shifted dates/prices on the line — e.g. a
  // qty bump pushing the parent's pickup date forward to a future-
  // stock day. Merged into `hasChanges` and rendered by
  // `BasketNotices` alongside loader-driven revalidation changes so
  // the user always sees what the system did on their behalf.
  clientChanges: BasketRevalidationChange[]
  setClientChanges: (changes: BasketRevalidationChange[]) => void
  clearClientChanges: () => void

  // Loading states
  isLoading: boolean
  isUpdating: boolean
  isRemoving: boolean
  isMetaUpdating: boolean

  // External loading — allows hooks outside the fetcher to trigger the global loader
  setExternalLoading: (loading: boolean) => void

  // Tracks which line IDs are mid-remove so BasketItem can show its own spinner
  pendingRemoveIds: Set<string>

  // Cart mutation actions
  updateItems: (items: BasketLineItemProps[]) => void
  removeItems: (items: BasketLineItemProps[]) => void
  updateMeta: (meta: Record<string, string | number | boolean>) => void
  updateAddress: (userAddress: UserAddressProps) => void

  // Dates modal state
  datesModalLineId: string | null
  openDatesModal: (lineId: string) => void
  closeDatesModal: () => void

  // Pickup modal state
  pickupModalLineId: string | null
  pickupSearchMap: Map<string, PickupSearchCache>
  openPickupModal: (lineId: string) => void
  closePickupModal: () => void
  setPickupSearch: (lineId: string, cache: PickupSearchCache) => void
  clearPickupSearch: (lineId: string) => void

  // Delivery date modal state
  deliveryDateModalLineId: string | null
  openDeliveryDateModal: (lineId: string) => void
  closeDeliveryDateModal: () => void

  // Address modal state
  addressModalOpen: boolean
  openAddressModal: () => void
  closeAddressModal: () => void

  // ------------------------------------------------------------------
  // Charges fetch state (from `useBrendaCharges`, hoisted)
  // ------------------------------------------------------------------
  //
  // One shared `useBrendaCharges` instance for every basket flow.
  // Flows (quantity change, fulfilment switch, delivery date modal,
  // pickup modal, dates modal) all submit through `submitCharges` and
  // read the current `charges` / `chargesLoading` / `chargesError`
  // from here. Each flow should call `clearCharges()` before its
  // first submit so the preserved original-dates window belongs to
  // the right line.
  charges: BrendaChargesResult | null
  chargesLoading: boolean
  chargesError: BrendaChargesErrorState | null
  chargesBankHolidays: HssBankHolidayItem[] | null
  submitCharges: (
    submit: BrendaChargesSubmit,
    options?: { preserveSide?: 'delivery' | 'pickup' }
  ) => void
  clearCharges: () => void
}

// ---------------------------------------------------------------------------
// Context + hook
// ---------------------------------------------------------------------------

const BasketContext = createContext<BasketContextValue | null>(null)

export const useBasketContext = (): BasketContextValue => {
  const ctx = useContext(BasketContext)
  if (!ctx) throw new Error('useBasketContext must be used within a BasketProvider')
  return ctx
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface BasketProviderProps {
  cart: LunarBasket | null
  revalidation?: BasketRevalidation | null
  children: ReactNode
}

/**
 * BasketProvider
 *
 * Single source of truth for every piece of basket state consumed
 * across the basket page and its modals. Owns:
 *
 *   1. **Cart mutations** — a serialised fetcher queue so consecutive
 *      `updateItems` / `removeItems` / `updateMeta` / `updateAddress`
 *      calls can't race each other. Requests fired while one is
 *      in-flight are queued in FIFO order and flushed when the
 *      fetcher returns to idle.
 *
 *   2. **Derived cart data** — memoised line grouping, item counts,
 *      free-delivery flag, same-day-delivery flag, title lookup, and
 *      revalidation summary (issues / changes) — all rebuilt only
 *      when `cart.included` changes.
 *
 *   3. **Modal state** — open/close for the dates, pickup, delivery
 *      date and address modals, plus per-line caches where needed
 *      (e.g. `pickupSearchMap` keeps a previously-fetched branch
 *      list around so re-opening the pickup modal doesn't re-fetch).
 *
 *   4. **Shared `useBrendaCharges` instance** — one hook for the
 *      whole basket, exposed via `charges` / `chargesLoading` /
 *      `chargesError` / `submitCharges` / `clearCharges`. Every
 *      basket flow (quantity change, fulfilment switch, dates modal,
 *      pickup modal, delivery date modal) reads and writes through
 *      this single instance, so bank-holiday caching, original-date
 *      preservation and in-flight state are shared. Flows guard
 *      their reads with a `productCode` + request-line-id check so
 *      line A never reacts to line B's response.
 *
 *   5. **External loading** — `setExternalLoading` lets non-fetcher
 *      flows (e.g. an in-flight charges request) contribute to the
 *      global `isLoading` flag so the page spinner behaves sensibly.
 */
export const BasketProvider = ({ cart, revalidation: externalRevalidation, children }: BasketProviderProps) => {
  // Debug logging
  const debugRef = useDebugLogger('DEBUG_CHECKOUT_FE')

  // Single fetcher for all cart mutations to prevent out-of-order updates
  const fetcher = useFetcher()

  // FIFO queue for serializing mutations — if a request is in-flight,
  // subsequent mutations are queued and processed in order
  const queueRef = useRef<{ body: string; action: string }[]>([])

  // Track which IDs are currently being removed so BasketItem can self-disable
  const [pendingRemoveIds, setPendingRemoveIds] = useState<Set<string>>(
    new Set()
  )

  // Track which action type is currently in-flight for granular loading states
  const [activeAction, setActiveAction] = useState<
    'update' | 'remove' | 'meta' | 'address' | null
  >(null)

  // Client-side issues/changes raised by basket flows. Declared up
  // here (above `submitMutation`) so that every cart mutation can
  // wipe both slices as it dispatches — that way a user action like
  // removing the problematic line naturally clears any notice the
  // previous action set, without each flow having to remember to
  // clear them by hand.
  const [clientIssues, setClientIssuesState] = useState<
    BasketRevalidationIssue[]
  >([])
  const [clientChanges, setClientChangesState] = useState<
    BasketRevalidationChange[]
  >([])

  // When the fetcher goes idle, clear remove IDs and flush the queue
  useEffect(() => {
    if (fetcher.state !== 'idle') return

    setPendingRemoveIds(prev => (prev.size > 0 ? new Set() : prev))
    setActiveAction(null)

    // Flush the next queued mutation if any are waiting
    if (queueRef.current.length > 0) {
      const next = queueRef.current.shift()!

      fetcher.submit(next.body, {
        method: 'post',
        encType: 'application/json',
        action: next.action,
      })
    }
  }, [fetcher.state, fetcher])

  // Submit immediately or enqueue if a request is already in-flight
  const submitMutation = useCallback(
    (body: string, action: string) => {
      // Any cart mutation is a new user action, so any client-side
      // issue/change notice left over from a previous flow is now
      // stale by definition — clear both slices here so every
      // mutation (remove, address change, meta write, dates modal,
      // pickup modal, etc.) self-clears the notice without each
      // flow having to remember. Flows that legitimately produce
      // fresh client state on the *same* action (e.g. the qty-change
      // flow's date-shift notice) call `setClientChanges` *after*
      // dispatching `updateItems`, so the set runs after this clear
      // in the same effect tick and wins.
      setClientIssuesState(prev => (prev.length === 0 ? prev : []))
      setClientChangesState(prev => (prev.length === 0 ? prev : []))

      if (fetcher.state !== 'idle') {
        queueRef.current.push({ body, action })
        return
      }

      fetcher.submit(body, {
        method: 'post',
        encType: 'application/json',
        action,
      })
    },
    [fetcher]
  )

  // ---------------------------------------------------------------------------
  // Derived cart data — only recomputed when cart.included changes
  // ---------------------------------------------------------------------------
  const { allLines, lineItems, itemsCount, hasFreeDelivery } = useMemo(() => {
    const allLines = buildCartLines(cart?.included)
    const lineItems = groupCartLines(allLines)
    const itemsCount = getCartItemsCount(allLines)
    const hasFreeDelivery = getCartHasFreeDelivery(lineItems)

    return { allLines, lineItems, itemsCount, hasFreeDelivery }
  }, [cart?.included])

  // Log the processed cart lines when debug is enabled
  useEffect(() => {
    if (!lineItems.length) return

    // Dumps every grouped line (parent + children) in full so any
    // meta field — `hire_damage_waiver`, `hire_deposit`, pickup info,
    // dates, raw attributes — is inspectable without cherry-picking
    // a subset at log time.
    debugRef.current('[BasketProvider] cart lines:', lineItems)

    // Cart-level `prices` as returned by Lunar — this is the block
    // that drives the totals UI (damage_waiver_total, sub_total,
    // deposit_total, etc). Useful for diffing "what we wrote on the
    // line meta" vs "what Lunar actually computed for the cart".
    debugRef.current(
      '[BasketProvider] cart prices:',
      cart?.data?.attributes?.prices ?? null
    )

    // Full per-line dump — attributes (meta, prices, quantity, etc.)
    // for every parent + child so any field discrepancy is visible
    // without cherry-picking a subset at log time.
    debugRef.current(
      '[BasketProvider] cart lines:',
      lineItems.map(group => ({
        id: group.parent.id,
        stockCode: group.parent.attributes.stock_code,
        attributes: group.parent.attributes,
        linked: group.linked.map(child => ({
          id: child.id,
          stockCode: child.attributes.stock_code,
          attributes: child.attributes,
        })),
      }))
    )
  }, [lineItems, cart, debugRef])

  // ---------------------------------------------------------------------------
  // Revalidation — from Brenda charges comparison (passed via prop)
  // ---------------------------------------------------------------------------
  // Test mode — only active when DEBUG_CHECKOUT_FE is enabled
  const debugMode = useRootEnvBoolean('DEBUG_CHECKOUT_FE')
  const [searchParams] = useSearchParams()
  const testIssues = debugMode && searchParams.has('hasIssues')
  const testChanges = debugMode && searchParams.has('hasChanges')

  // Build mock revalidation data from real line items for testing
  const revalidation = useMemo(() => {
    if (testIssues && lineItems.length) {
      const first = lineItems[0].parent

      return {
        hasIssues: true,
        issues: [
          {
            code: 'outOfStock',
            lineId: first.id,
            sku: first.attributes.stock_code,
            title: first.title,
            fulfilment: String(first.attributes.meta?.selected_fulfilment ?? ''),
          },
        ],
        hasChanges: false,
        changes: [],
      }
    }

    if (testChanges && lineItems.length) {
      const first = lineItems[0].parent

      return {
        hasChanges: true,
        changes: [
          {
            lineId: first.id,
            sku: first.attributes.stock_code,
            title: first.title,
            changes: {
              price: { old: 5000, new: 6500 },
              deliveryDate: { old: '2026-04-01', new: '2026-04-03' },
            },
          },
        ],
        hasIssues: false,
        issues: [],
      }
    }

    return externalRevalidation ?? null
  }, [externalRevalidation, testIssues, testChanges, lineItems])

  // Setters/clearers for the client-side state declared above. The
  // state itself is hoisted up near `activeAction` so `submitMutation`
  // can wipe both slices on every cart mutation — see the comment
  // there for why. These wrappers just keep the public API stable
  // (`setClientIssues([issue])` / `clearClientIssues()` etc.) so
  // callers don't have to know about the raw state setter.
  const setClientIssues = useCallback((issues: BasketRevalidationIssue[]) => {
    setClientIssuesState(issues)
  }, [])

  const clearClientIssues = useCallback(() => {
    setClientIssuesState(prev => (prev.length === 0 ? prev : []))
  }, [])

  const setClientChanges = useCallback(
    (changes: BasketRevalidationChange[]) => {
      setClientChangesState(changes)
    },
    []
  )

  const clearClientChanges = useCallback(() => {
    setClientChangesState(prev => (prev.length === 0 ? prev : []))
  }, [])

  const hasIssues = useMemo(() => {
    return getCartHasIssues(revalidation) || clientIssues.length > 0
  }, [revalidation, clientIssues])

  const hasChanges = useMemo(() => {
    return getCartHasChanges(revalidation) || clientChanges.length > 0
  }, [revalidation, clientChanges])

  const titleByLineId = useMemo(() => {
    return buildTitleByLineId(lineItems)
  }, [lineItems])

  // True when any line item has delivery selected with today's date
  const hasSameDayDelivery = useMemo(() => {
    if (!lineItems.length) return false

    const todayUk = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/London',
    }).format(new Date())

    return lineItems.some(({ parent }) => {
      const meta = parent?.attributes?.meta
      if (!meta) return false

      return (
        meta.selected_fulfilment === 'delivery' &&
        meta.delivery_date === todayUk
      )
    })
  }, [lineItems])

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const updateItems = useCallback(
    (items: BasketLineItemProps[]) => {
      setActiveAction('update')
      submitMutation(JSON.stringify({ items }), '/api/basket/items-update')
    },
    [submitMutation]
  )

  const removeItems = useCallback(
    (items: BasketLineItemProps[]) => {
      const ids = new Set(
        items.map((i) => i.id).filter((id): id is string => Boolean(id))
      )
      setPendingRemoveIds(ids)
      setActiveAction('remove')
      submitMutation(JSON.stringify({ items }), '/api/basket/items-remove')
    },
    [submitMutation]
  )

  const updateMeta = useCallback(
    (meta: Record<string, string | number | boolean>) => {
      setActiveAction('meta')
      submitMutation(JSON.stringify({ meta }), '/api/basket/meta')
    },
    [submitMutation]
  )

  const updateAddress = useCallback(
    (userAddress: UserAddressProps) => {
      setActiveAction('address')
      submitMutation(JSON.stringify({ userAddress }), '/api/basket/address')
    },
    [submitMutation]
  )

  // ---------------------------------------------------------------------------
  // Dates modal — single instance, no caching
  // ---------------------------------------------------------------------------
  const [datesModalLineId, setDatesModalLineId] = useState<string | null>(null)

  const openDatesModal = useCallback((lineId: string) => {
    setDatesModalLineId(lineId)
  }, [])

  const closeDatesModal = useCallback(() => {
    setDatesModalLineId(null)
  }, [])

  // ---------------------------------------------------------------------------
  // Pickup modal — single instance, results cached per line ID
  // ---------------------------------------------------------------------------
  const [pickupModalLineId, setPickupModalLineId] = useState<string | null>(null)
  const [pickupSearchMap, setPickupSearchMap] = useState<Map<string, PickupSearchCache>>(
    new Map()
  )

  const openPickupModal = useCallback((lineId: string) => {
    setPickupModalLineId(lineId)
  }, [])

  const closePickupModal = useCallback(() => {
    setPickupModalLineId(null)
  }, [])

  const setPickupSearch = useCallback(
    (lineId: string, cache: PickupSearchCache) => {
      setPickupSearchMap((prev) => new Map(prev).set(lineId, cache))
    },
    []
  )

  const clearPickupSearch = useCallback((lineId: string) => {
    setPickupSearchMap((prev) => {
      const next = new Map(prev)

      next.delete(lineId)

      return next
    })
  }, [])

  // ---------------------------------------------------------------------------
  // Delivery date modal — single instance, buy products only
  // ---------------------------------------------------------------------------
  const [deliveryDateModalLineId, setDeliveryDateModalLineId] = useState<
    string | null
  >(null)

  const openDeliveryDateModal = useCallback((lineId: string) => {
    setDeliveryDateModalLineId(lineId)
  }, [])

  const closeDeliveryDateModal = useCallback(() => {
    setDeliveryDateModalLineId(null)
  }, [])

  // ---------------------------------------------------------------------------
  // Address modal — simple open/close, no per-line state
  // ---------------------------------------------------------------------------
  const [addressModalOpen, setAddressModalOpen] = useState(false)

  const openAddressModal = useCallback(() => {
    setAddressModalOpen(true)
  }, [])

  const closeAddressModal = useCallback(() => {
    setAddressModalOpen(false)
  }, [])

  // ---------------------------------------------------------------------------
  // External loading — hooks outside the fetcher can trigger the global loader
  // ---------------------------------------------------------------------------
  const [externalLoading, setExternalLoading] = useState(false)

  // ---------------------------------------------------------------------------
  // Charges fetch — one shared `useBrendaCharges` instance for every basket
  // flow. Hooks like `useBasketLine`, `useBasketFulfilment`, etc. read from
  // here instead of instantiating their own copy so bank-holiday caching,
  // in-flight state and original-date preservation are shared.
  // ---------------------------------------------------------------------------
  const {
    submitBrendaCharges: submitCharges,
    result: charges,
    loading: chargesLoading,
    error: chargesError,
    clear: clearCharges,
    bankHolidays: chargesBankHolidays,
  } = useBrendaCharges()

  // ---------------------------------------------------------------------------
  // Aggregated loading states
  // ---------------------------------------------------------------------------
  const isBusy = fetcher.state !== 'idle'
  const isUpdating = isBusy && activeAction === 'update'
  const isRemoving = isBusy && activeAction === 'remove'
  const isMetaUpdating = isBusy && activeAction === 'meta'
  const isLoading = isBusy || externalLoading

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // Memoize the context value to prevent unnecessary consumer re-renders
  const contextValue = useMemo<BasketContextValue>(
    () => ({
      cart,
      allLines,
      lineItems,
      itemsCount,
      hasFreeDelivery,
      hasSameDayDelivery,
      revalidation,
      hasIssues,
      hasChanges,
      titleByLineId,
      clientIssues,
      setClientIssues,
      clearClientIssues,
      clientChanges,
      setClientChanges,
      clearClientChanges,
      isLoading,
      isUpdating,
      isRemoving,
      isMetaUpdating,
      pendingRemoveIds,
      updateItems,
      removeItems,
      updateMeta,
      updateAddress,
      datesModalLineId,
      openDatesModal,
      closeDatesModal,
      pickupModalLineId,
      pickupSearchMap,
      openPickupModal,
      closePickupModal,
      setPickupSearch,
      clearPickupSearch,
      deliveryDateModalLineId,
      openDeliveryDateModal,
      closeDeliveryDateModal,
      addressModalOpen,
      openAddressModal,
      closeAddressModal,
      setExternalLoading,
      charges,
      chargesLoading,
      chargesError,
      chargesBankHolidays,
      submitCharges,
      clearCharges,
    }),
    [
      cart,
      allLines,
      lineItems,
      itemsCount,
      hasFreeDelivery,
      hasSameDayDelivery,
      revalidation,
      hasIssues,
      hasChanges,
      titleByLineId,
      clientIssues,
      setClientIssues,
      clearClientIssues,
      clientChanges,
      setClientChanges,
      clearClientChanges,
      isLoading,
      isUpdating,
      isRemoving,
      isMetaUpdating,
      pendingRemoveIds,
      updateItems,
      removeItems,
      updateMeta,
      updateAddress,
      datesModalLineId,
      openDatesModal,
      closeDatesModal,
      pickupModalLineId,
      pickupSearchMap,
      openPickupModal,
      closePickupModal,
      setPickupSearch,
      clearPickupSearch,
      deliveryDateModalLineId,
      openDeliveryDateModal,
      closeDeliveryDateModal,
      addressModalOpen,
      openAddressModal,
      closeAddressModal,
      setExternalLoading,
      charges,
      chargesLoading,
      chargesError,
      chargesBankHolidays,
      submitCharges,
      clearCharges,
    ]
  )

  return (
    <BasketContext.Provider value={contextValue}>
      {children}
    </BasketContext.Provider>
  )
}
